import {
    Component,
    CUSTOM_ELEMENTS_SCHEMA,
    NO_ERRORS_SCHEMA,
    Output,
    OnInit,
    effect,
    EventEmitter,
    Renderer2,
    ViewChild,
    ElementRef,
    OnDestroy
} from "@angular/core";
import {TranslatePipe, TranslateService} from "@ngx-translate/core";
import {chartOptions} from "./chartSettings/options.chart";
import {chartData} from "./chartSettings/chart.data";
import 'chartjs-adapter-moment';
import {Chart as ChartJS, TimeScale,} from "chart.js/auto";
import moment, {Moment} from 'moment-timezone';
import {of, Subscription} from "rxjs";
import {catchError} from "rxjs/operators";
import {CommonModule, NgClass, NgStyle} from "@angular/common";
import {ChartServices} from "@/app/services/chart-serivces.service";
import {
    ChartData,
    ChartDatasetsObject,
    Contact,
    NodePerformanceForChart,
    ViewModel,
} from "@/app/model/chart";
import {baselineAdjustmentWhitelist as productsWithBaselineAdjustment} from "./baseLine/baselineAdjustmentWhitelist";
import {environment} from "@/environments/environment";
import {EventService} from "@/app/services/event.service";
import {showMoreCss} from "@/app/stylesFormat/stylesFormat";
import {Registration} from "@/app/model/interface/registration";
import {getTzAbbrForce} from "@/app/common/common";
import {verticalHoverLine, yTitleTopPlugin, annotationBoxPlugin} from "./chartSettings/utilities";
import {CookieService} from "@/app/services/cookie.service";
import {BannerDetails, ChartExportFormat, ChartExportLegendItem} from "@/app/model/common";
import {EventWindowPipe} from "@/app/pipe/event-window.pipe";
import parsePhoneNumberFromString from 'libphonenumber-js';
import {RightClickDirective} from '@/app/directives/right-click.directive';
import {Clipboard} from '@angular/cdk/clipboard';
import {NodePerformance, NodePerformanceAPI, Product} from "@/app/model";
import {EventNodeAPI} from "@/app/model/interface/event-node";
import {Event} from "@/app/model/interface/event";
import {MatIconModule} from "@angular/material/icon";
import {useTranslationWithFallback} from "@/app/shared/translation";
import {ChartExportService} from "@/app/services/chart-export.service";
import {NativeService} from "@/app/services/native.service";

@Component({
    selector: "app-chart",
    templateUrl: "./chart.component.html",
    styleUrls: ["./chart.component.scss"],
    standalone: true,
    imports: [CommonModule, NgClass, NgStyle, TranslatePipe, RightClickDirective, MatIconModule],
    providers: [EventWindowPipe],
    schemas: [
        CUSTOM_ELEMENTS_SCHEMA,
        NO_ERRORS_SCHEMA
    ]
})
export class ChartComponent implements OnInit, OnDestroy {

    private defaultRampingPeriodInMinutes: number = 20;
    private pendingChartMarginIntervals: number = 20;
    public visibleCount = 3;
    public data: ChartData;
    public options: any;
    public chart: any;
    public openDispatch: boolean = true;
    public openRules: boolean = false;
    public showMore: boolean = true;
    public listDownload: boolean = false;
    public iconName: string = 'arrow-down';
    public iconNameRules: string = 'arrow-down';
    public openListElement: boolean = false;
    public viewModel: ViewModel;
    public dropdownDownload: string[];
    public eventNode: EventNodeAPI;
    public event: Event | null;
    public isChartLoading: boolean = false;
    public isContactsLoading: boolean = false;
    public isRegistrationLoading: boolean = false;
    public registration: Registration;
    public noDataActiveNode: boolean = false;
    public eventStatus: string;
    public eventPerformance: NodePerformanceAPI[] = [];

    private readonly subscriptions$: Subscription[] = [];
    private currentlySubcribedEventNodeId: string | null = null;
    public product: Product | undefined = undefined;
    private readonly timeChart = {
        startRamp: 0,
        endRamp: 0,
        startBaseline: 0,
        endBaseline: 0,
    }
    public locale: string;

    public isMobile = false;
    public isDispatchDetailsOpen = false;

    //title text
    public activeDispatchText = {
        site: '',
        siteDetails: '',
        startAndEndTime: '',
        date: {
            day: '',
            timeStart: '',
            timeEnd: '',
        },
        program: '',
        organization_name: '',
        energy_reduction_plan: ''
    }

    @ViewChild('openListDownloadButton') openListDownloadButton: ElementRef;
    @ViewChild('listDownloadDropdown') listDownloadDropdown: ElementRef;

    public activeTooltipEmailId: string | null = null;

    public bannerDetails: BannerDetails = {
        title: 'No performance data available at this location.',
        description: 'Please contact EnelX Customer Support.',
        ...bannerStyle.grey
    };
    // card info
    public bannerDetailsStatusType: any;

    public contacts: Contact[] = [];
    @Output() public sidebarOpenRequested = new EventEmitter<void>();

    constructor(
        private readonly chartService: ChartServices,
        private readonly eventService: EventService,
        private cookieService: CookieService,
        private readonly translate: TranslateService,
        private readonly eventWindowPipe: EventWindowPipe,
        private readonly renderer: Renderer2,
        private clipboard: Clipboard,
        private readonly chartExportService: ChartExportService,
        private readonly nativeService: NativeService,
    ) {
        this.isMobile = this.nativeService.isMobileDevice();
        this.isDispatchDetailsOpen = !this.isMobile;
        ChartJS.register(annotationBoxPlugin, TimeScale, yTitleTopPlugin, verticalHoverLine);

        effect(() => {
            const selectedNode = this.eventService.selectedEventNodeSource();
            const selectedEvent = this.eventService.selectedEvent();
            if (selectedNode && selectedEvent) {
                this.setData(selectedEvent, selectedNode);
            }
        });

        this.renderer.listen('window', 'click', (e: any) => {
            if (!this.openListDownloadButton?.nativeElement.contains(e.target) && !this.listDownloadDropdown?.nativeElement.contains(e.target)) {
                this.listDownload = false;
            }
        });
    }

    ngOnInit(): void {
        this.locale = this.cookieService.getI18NLocale();

        useTranslationWithFallback(this.translate, this.locale, this.cookieService.getI18NBrand()).subscribe(() => {
            this.setTranslations();
        });

        const eventNodeUpdateSub = this.eventService.eventNodeUpdate$.subscribe((updatedNode: EventNodeAPI) => {
            if (this.eventNode && updatedNode.event_node_id === this.eventNode.event_node_id) {
                this.eventNode = updatedNode;
                if (this.event && this.product) {
                    this.bannerDetails = this.createBanner(this.event, this.product);
                }
            }
        });
        this.subscriptions$.push(eventNodeUpdateSub);
    }

    ngOnDestroy(): void {
        this.destroyChart();
        if (this.currentlySubcribedEventNodeId) {
            this.chartService.unsubscribeFromNodePerformance(this.currentlySubcribedEventNodeId);
        }
        if (this.subscriptions$.length > 0) this.subscriptions$.forEach((subs: Subscription) => subs.unsubscribe());
    }

    setTranslations() {
        this.bannerDetailsStatusType = {
            NO_DATA: {
                title: this.translate.instant("BANNER.PRIMARY.NO_DATA"),
                description: this.translate.instant("BANNER.SECONDARY.NO_DATA"),
                ...bannerStyle.grey
            },
            AFTER: {
                PERFORMING: {
                    title: this.translate.instant("BANNER.PRIMARY.AFTER.PERFORMING"),
                    description: '',
                    ...bannerStyle.green
                },
                PERFORMING_NEAR_EXPECTATION: {
                    title: this.translate.instant("BANNER.PRIMARY.AFTER.PERFORMING_NEAR_EXPECTATION"),
                    description: '',
                    ...bannerStyle.orange
                },
                UNDER_PERFORMING: {
                    title: this.translate.instant("BANNER.PRIMARY.AFTER.UNDER_PERFORMING"),
                    description: '',
                    ...bannerStyle.red
                },
                NO_PERF_NUMBERS: {
                    title: this.translate.instant("BANNER.PRIMARY.NO_DATA"),
                    description: this.translate.instant("BANNER.SECONDARY.NO_DATA"),
                    ...bannerStyle.grey
                },
                ONLY_PERF_AVAILABLE: {
                    title: this.translate.instant("BANNER.PRIMARY.NO_DATA"),
                    description: '',
                    ...bannerStyle.grey
                },
                ONLY_TARGET_AVAILABLE: {
                    title: this.translate.instant("BANNER.PRIMARY.NO_DATA"),
                    description: '',
                    ...bannerStyle.grey
                },
                NO_ACTIVE_DATA: {
                    title: this.translate.instant("BANNER.TRAY.NO_DATA.MESSAGE"),
                    description: this.translate.instant("BANNER.SECONDARY.NO_DATA"),
                    ...bannerStyle.grey
                },
            },
            NOT_AFTER: {
                PERFORMING: {
                    title: this.translate.instant("BANNER.PRIMARY.BEFORE.PERFORMING"),
                    description: '',
                    ...bannerStyle.green
                },
                PERFORMING_NEAR_EXPECTATION: {
                    title: this.translate.instant("BANNER.PRIMARY.BEFORE.PERFORMING_NEAR_EXPECTATION"),
                    description: '',
                    ...bannerStyle.orange
                },
                UNDER_PERFORMING: {
                    title: this.translate.instant("BANNER.PRIMARY.BEFORE.UNDER_PERFORMING"),
                    description: '',
                    ...bannerStyle.red
                },
                NO_PERF_NUMBERS: {
                    title: this.translate.instant("BANNER.PRIMARY.NO_DATA"),
                    description: this.translate.instant("BANNER.SECONDARY.NO_DATA"),
                    ...bannerStyle.grey
                },
                ONLY_PERF_AVAILABLE: {
                    title: this.translate.instant("BANNER.PRIMARY.NO_DATA"),
                    description: '',
                    ...bannerStyle.grey
                },
                ONLY_TARGET_AVAILABLE: {
                    title: this.translate.instant("BANNER.PRIMARY.NO_DATA"),
                    description: '',
                    ...bannerStyle.grey
                },
                NO_ACTIVE_DATA: {
                    title: this.translate.instant("BANNER.TRAY.NO_DATA.MESSAGE"),
                    description: this.translate.instant("BANNER.SECONDARY.NO_DATA"),
                    ...bannerStyle.grey
                },
            }
        }
        this.bannerDetails = this.bannerDetailsStatusType['NO_DATA'];
        this.dropdownDownload = [
            this.translate.instant("DISPATCH_PERFORMANCE.DOWNLOAD.DOWNLOAD_JPEG"),
            this.translate.instant("DISPATCH_PERFORMANCE.DOWNLOAD.DOWNLOAD_SVG"),
            this.translate.instant("DISPATCH_PERFORMANCE.DOWNLOAD.DOWNLOAD_PDF"),
        ];
    }

    setData(selectedEvent: Event, selectedNode: EventNodeAPI) {

        if (!selectedNode || !selectedEvent || !selectedEvent.product) return;
        console.log('Set Data for eventNode: ', selectedNode.event_node_id);

        if (this.currentlySubcribedEventNodeId && this.currentlySubcribedEventNodeId !== selectedNode.event_node_id) {
            this.chartService.unsubscribeFromNodePerformance(this.currentlySubcribedEventNodeId);
        }

        this.event = selectedEvent;
        this.eventNode = selectedNode;
        this.product = selectedEvent.product;

        const subscribeToRealtimeUpdates = selectedEvent.event_progress_status !== 'AFTER';
        try {
            this.setActiveDispatchText(selectedEvent);
            this.bannerDetails = this.createBanner(selectedEvent, this.product);
            const additionalChartData = this.calculateAdditionalChartData(selectedEvent, this.product);

            this.isContactsLoading = true;
            this.getContacts(selectedNode);

            if (selectedNode.registration_id) {
                this.isRegistrationLoading = true;
                this.getRegistration(selectedNode.registration_id);
            }

            this.isChartLoading = true;
            this.chartService.getNodePerformances(
                selectedNode,
                this.product,
                additionalChartData.chartStartTime,
                additionalChartData.chartEndTime,
                subscribeToRealtimeUpdates
            )
                .pipe(this.catchError())
                .subscribe((eventPerformance: NodePerformance[]) => {
                    this.isChartLoading = false;
                    this.handleChart(selectedEvent, eventPerformance, additionalChartData);
                });

            this.currentlySubcribedEventNodeId = subscribeToRealtimeUpdates ? selectedNode.event_node_id : null;
        } catch (error) {
            console.error('Error loading data:', error);
            this.isChartLoading = false;
            this.isContactsLoading = false;
            this.isRegistrationLoading = false;
        }
    }

    sortContact() {
        this.contacts.sort((a: Contact, b: Contact) => {
            const isA_247AndPrimary =
                a.twentyfour_seven_contact && a.primary_contact;
            const isB_247AndPrimary =
                b.twentyfour_seven_contact && b.primary_contact;

            if (b.phone) {
                // 6.1 — 24/7 + Primary first
                if (isA_247AndPrimary && !isB_247AndPrimary) return -1;
                if (!isA_247AndPrimary && isB_247AndPrimary) return 1;

                // 6.2 — Then 24/7 only
                if (a.twentyfour_seven_contact && !b.twentyfour_seven_contact)
                    return -1;
                if (!a.twentyfour_seven_contact && b.twentyfour_seven_contact)
                    return 1;

                // 6.3 — Then Primary only
                if (a.primary_contact && !b.primary_contact) return -1;
                if (!a.primary_contact && b.primary_contact) return 1;

                // 6.4 — Finally, sort all by full_name
                const nameA = a.full_name?.trim() || "";
                const nameB = b.full_name?.trim() || "";
                return nameA.localeCompare(nameB);
            }
            return -1;
        });
    }

    buildLocalizedContactFullName() {
        this.contacts.forEach((contact: Contact) => {
            const easternOrder = ['zh_TW', 'zh_CN', 'zh_HK', 'ja_JP', 'ko_KR'];
            const contactLocale = contact.default_locale || 'en_US';
            const firstName = !!contact.first_name ? (contact.first_name as any)[contactLocale] || contact.first_name?.en_US || '' : '';
            const lastName = !!contact.last_name ? (contact.last_name as any)[contactLocale] || contact.last_name?.en_US || '' : '';
            const locatilzedFullName = easternOrder.includes(contactLocale) ? `${lastName}${firstName}`.trim() : `${firstName} ${lastName}`.trim();
            contact.full_name = !!locatilzedFullName ? locatilzedFullName : contact.full_name;
        });
    }

    getContacts(eventNode: EventNodeAPI) {
        this.chartService.getContacts(eventNode.site_id).pipe(
            this.catchError()).subscribe((contacts: Contact[]) => {
            this.isContactsLoading = false;
            this.contacts = contacts;
            this.sortContact();
            this.buildLocalizedContactFullName();
            setTimeout(() => {
                showMoreCss();
            })
        });
    }

    catchError = () => catchError((err: any) => {
        return of(err);
    });

    setActiveDispatchText = (selectedEvent: Event) => {
        const {
            program_name,
            program_time_zone_abbr,
            ems_program_display_label,
            event_start_dttm_utc,
            event_end_dttm_utc,
            full_time_zone
        } = selectedEvent;
        let time_zone = program_time_zone_abbr;
        if (time_zone.startsWith('+') || time_zone.startsWith('-')) {
            time_zone = `GMT ${time_zone}`
        }
        const {site_name, organization_name} = this.eventNode;
        this.activeDispatchText.site = site_name ?? '';
        this.activeDispatchText.siteDetails = this.eventNode.registration?.display_label ?? '';
        const cookieLocale = this.cookieService.getI18NLocale();
        const localeParts = cookieLocale.split('-');
        const localeKey = localeParts.length === 2
            ? `${localeParts[0].toLowerCase()}_${localeParts[1].toUpperCase()}`
            : cookieLocale;
        this.activeDispatchText.organization_name = organization_name?.[localeKey] || organization_name?.['en_US'] || '';
        this.activeDispatchText.startAndEndTime = this.eventWindowPipe.transform(
            event_start_dttm_utc,
            event_end_dttm_utc,
            full_time_zone,
            program_time_zone_abbr,
            'absolute'
        );
        this.activeDispatchText.program = program_name?.[localeKey] || ems_program_display_label;
    }

    createChart = (
        event: Event,
        product: Product,
        eventPerformanceData: NodePerformanceForChart | null,
        eventNode: EventNodeAPI,
        eventNodePerformance: NodePerformance[],
        additionalChartData: AdditionalChartData
    ) => {
        this.destroyChart();
        const canvas = document.getElementById('ADChart') as HTMLCanvasElement | null;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const gradient = ctx?.createLinearGradient(0, 0, 0, 300);
        gradient?.addColorStop(0, 'rgba(53,153,143, 0.2)');
        gradient?.addColorStop(1, 'rgba(53,153,143, 0.2)');
        const {axisEndMs, granularityMinutes: xAxisGranularity} = this.resolveAxisEndMs(eventNodePerformance, additionalChartData);
        if (eventPerformanceData) {
            this.data = chartData(
                eventPerformanceData.labels,
                eventPerformanceData.datasetsBaseline,
                eventPerformanceData.datasetsTarget,
                eventPerformanceData.datasetsTargetLowerBound,
                eventPerformanceData.datasetsTargetUpperBound,
                eventPerformanceData.datasetsDemand,
                gradient,
                eventNode,
                product,
                additionalChartData,
                axisEndMs
            );
        }
        this.options = chartOptions(
            product,
            event,
            eventNode,
            this.cookieService.getI18NLocale(),
            eventNodePerformance,
            xAxisGranularity,
            this.translate,
            additionalChartData.chartStartMs,
            axisEndMs,
            this.isMobile
        );
        this.chart = new ChartJS(ctx, {type: 'line', data: this.data, options: this.options.options});
    }

    // Pending events have no chartEndTime (open-ended, data-driven). For the axis/ghost-anchor
    // we still leave a margin of pendingChartMarginIntervals x-axis-label intervals past the
    // last interval_dttm_utc received in the EPS response. The granularity used to size that
    // margin is computed against the real (unpadded) data span and returned alongside axisEndMs
    // so callers don't recompute it against the now-padded span, which would fall into a
    // coarser detectXAxisGranularity bucket than the data actually warrants.
    resolveAxisEndMs(eventNodePerformance: NodePerformance[], additionalChartData: AdditionalChartData): { axisEndMs: number | null; granularityMinutes: number } {
        if (additionalChartData.chartEndTime) {
            const granularityMinutes = this.detectXAxisGranularity(additionalChartData.chartStartMs, additionalChartData.chartEndMs, additionalChartData.reportingInterval);
            return {axisEndMs: additionalChartData.chartEndMs, granularityMinutes};
        }
        if (!eventNodePerformance.length) {
            const granularityMinutes = this.detectXAxisGranularity(additionalChartData.chartStartMs, null, additionalChartData.reportingInterval);
            return {axisEndMs: null, granularityMinutes};
        }
        const lastDataMs = eventNodePerformance[eventNodePerformance.length - 1].interval.time;
        const granularityMinutes = this.detectXAxisGranularity(additionalChartData.chartStartMs, lastDataMs, additionalChartData.reportingInterval);
        const axisEndMs = lastDataMs + this.pendingChartMarginIntervals * granularityMinutes * 60000;
        return {axisEndMs, granularityMinutes};
    }

    detectXAxisGranularity(chartStartMs: number, chartEndMs: number | null, reportingInterval: number) {
        const durationMs = (chartEndMs ?? Date.now()) - chartStartMs;
        const durationMins = durationMs / 60000;

        const reportingIntervalMins = reportingInterval / 60000;

        let granularity = 5;
        if (durationMins < 60) {
            granularity = 5;
        } else if (durationMins < 120) {
            granularity = 10;
        } else if (durationMins < 210) {
            granularity = 15;
        } else {
            granularity = 30;
        }

        return reportingIntervalMins > granularity ? reportingIntervalMins : granularity;
    }

    destroyChart() {
        if (this.chart) {
            this.chart.destroy();
            this.chart = undefined;
        }

        const existing = ChartJS.getChart('ADChart');
        if (existing) {
            existing.destroy();
        }
    }

    showLineChart(index: number) {
        if (this.data) {
            return this.data.datasets[index].data.every((lineData) => lineData.y === null || !lineData.x);
        } else {
            return [];
        }
    }

    private setTextBanner = (
        event: Event,
        product: Product,
        expected_capacity_value: number,
        formattedDate: any,
        eventFinished: string
    ): string => {
        const {event_progress_status} = event;
        const {
            last_current_performance_value,
            last_current_performance_percentage,
            last_current_performance_uom
        } = this.eventNode;
        let unit = product.prez_conf?.prefered_prez_demand_uom ?? last_current_performance_uom;
        const isKw = unit.toLowerCase() === "kw";
        unit = isKw ? 'kW' : 'MW';
        const performanceValue =
            event_progress_status === "DURING" || event_progress_status === "BEFORE"
                ? this.formatBannerPowerValue(last_current_performance_value, isKw)
                : this.eventService.getOverallPerformance(
                    product,
                    this.eventNode,
                    this.eventNode.registration_type,
                    this.locale
                );
        const averagePerformancePercent =
            event_progress_status === "DURING" || event_progress_status === "BEFORE"
                ? Intl.NumberFormat(this.locale, {
                    maximumFractionDigits: 0
                }).format(last_current_performance_percentage)
                : this.eventService.getOverallPercentage(
                    product,
                    this.eventNode,
                    this.eventNode.registration_type,
                    this.locale
                );
        const expectedValue = isKw
            ? this.formatBannerPowerValue(expected_capacity_value, true)
            : this.formatBannerPowerValue(expected_capacity_value, false);

        if (['ONLY_PERF_AVAILABLE', 'ONLY_TARGET_AVAILABLE', 'NO_PERF_NUMBERS'].includes(this.eventStatus)) {
            return this.bannerDetailsStatusType['NO_DATA'].description;
        }

        return this.translate.instant(`BANNER.CBL.HAS_STATUS.${eventFinished}.${formattedDate ? 'TIMESTAMP' : 'NO_TIMESTAMP'}`, {
            performanceValue,
            performanceUOM: unit,
            performancePercentage: averagePerformancePercent,
            capacityValue: expectedValue,
            capacityUOM: unit,
            formattedDate
        })
    };

    private setTextBannerLoadToDrop = (
        event: Event,
        product: Product,
        formattedDate: any,
        eventFinished: string
    ): string => {
        const {event_progress_status} = event;
        const {
            last_current_metered_value,
            last_current_performance_percentage,
            last_current_performance_uom,
            last_current_target_value,
            last_current_performance_value,
        } = this.eventNode;
        const {
            prez_conf: {prefered_prez_demand_uom},
        } = product;
        const productUOM = prefered_prez_demand_uom ?? last_current_performance_uom;
        const isKw = productUOM.toLowerCase() === "kw";
        const demandValue =
            event_progress_status === "DURING" || event_progress_status === "BEFORE"
                ? Intl.NumberFormat(this.locale, {maximumFractionDigits: isKw ? 0 : 2}).format(
                    isKw ? Math.abs(last_current_metered_value) : Math.abs(last_current_metered_value) / 1000)
                : this.eventService.getOverallPerformance(
                    product,
                    this.eventNode,
                    this.eventNode.registration_type,
                    this.locale
                );
        let averagePerformancePercent =
            event_progress_status === "DURING" || event_progress_status === "BEFORE"
                ? Intl.NumberFormat(this.locale, {maximumFractionDigits: 0}).format(
                    Math.abs(last_current_performance_percentage))
                : this.eventService.getOverallPercentage(
                    product,
                    this.eventNode,
                    this.eventNode.registration_type,
                    this.locale
                )
        const performanceValue = this.formatBannerPowerValue(
            Math.abs(last_current_performance_value),
            isKw
        );
        const lastTargetValue = this.formatBannerPowerValue(
            Math.abs(last_current_target_value),
            isKw
        );

        return this.translate.instant(`BANNER.SECONDARY.DROP_TO.${eventFinished}.${this.eventStatus}.${formattedDate ? 'TIMESTAMP' : 'NO_TIMESTAMP'}`, {
            targetValue: lastTargetValue,
            demandValue,
            demandUOM: productUOM,
            performanceValue,
            performanceUOM: productUOM,
            performancePercentage: averagePerformancePercent,
            formattedDate
        });
    }

    private formatBannerPowerValue(value: number, isKw: boolean): string {
        const fractionDigits = isKw ? 0 : 3;
        const normalizedValue = isKw ? value : value / 1000;
        return Intl.NumberFormat(this.locale, {
            minimumFractionDigits: fractionDigits,
            maximumFractionDigits: fractionDigits,
        }).format(normalizedValue);
    }

    private formatDate(last_performance_time: string, event_time_zone: string, program_time_zone: string) {
        let time_zone = program_time_zone;
        if (time_zone.startsWith('+') || time_zone.startsWith('-')) {
            time_zone = `GMT ${time_zone}`
        }
        const tzAbbr = getTzAbbrForce(event_time_zone || time_zone, last_performance_time ? moment(last_performance_time).toDate() : undefined);
        const options: Intl.DateTimeFormatOptions = {
            timeStyle: "short",
            timeZone: event_time_zone,
            hour12: undefined,
        };

        const formattedDate = last_performance_time
            ? `${new Intl.DateTimeFormat(this.locale, options).format(moment(last_performance_time).valueOf())} ${tzAbbr}`
            : "";
        return formattedDate;
    }

    calculateAdditionalChartData(event: Event, product: Product): AdditionalChartData {
        const eventNodeStartTime = moment(this.eventNode.event_node_start_dttm_utc);
        const eventStartTime = moment(event.event_start_dttm_utc);

        const rampingUpPeriodInMinutes = product.dispatch_conf.ramping_period
            ? (product.dispatch_conf.ramping_period / (1000 * 60))
            : this.defaultRampingPeriodInMinutes;
        const maxMinutes = Math.max(rampingUpPeriodInMinutes, product.bonus_minutes || 0)
        const chartStartTime = eventNodeStartTime.clone().subtract(maxMinutes, 'minutes');

        const postRampingPeriodInMinutes = product.dispatch_conf.post_ramping_period
            ? (product.dispatch_conf.post_ramping_period / (1000 * 60))
            : this.defaultRampingPeriodInMinutes;
        const postMaxMinutes = Math.max(postRampingPeriodInMinutes, product.dispatch_conf.post_bonus_minutes || 0)

        let bonusStartTime = null;
        let bonusEndTime = null;
        if (product.bonus_minutes) {
            bonusStartTime = eventStartTime.clone().subtract(product.bonus_minutes || 0, 'minutes');
            bonusEndTime = eventStartTime.clone();
        }

        const rampUpStartTime = eventNodeStartTime.clone().subtract(maxMinutes, 'minutes');
        const rampUpEndTime = eventNodeStartTime.clone();

        const endTimeString = this.eventNode?.event_node_end_dttm_utc ||
            this.event?.event_end_dttm_utc ||
            this.event?.event_node_statistics?.end_dttm_utc;

        // No backend end time yet (event still pending): leave the chart open-ended
        // so it only ever shows the data points the API actually has.
        let chartEndTime: moment.Moment | null = null;
        let rampDownStartTime = null;
        let rampDownEndTime = null;

        if (endTimeString != null) {
            chartEndTime = moment(endTimeString).add(postMaxMinutes, 'minutes');
            rampDownStartTime = moment(endTimeString);
            rampDownEndTime = rampDownStartTime.clone().add(postMaxMinutes, 'minutes');
        }

        const chartStartMs = chartStartTime.valueOf();
        const chartEndMs = chartEndTime ? chartEndTime.valueOf() : null;

        return {
            chartStartTime: chartStartTime,
            chartEndTime: chartEndTime,
            chartStartMs,
            chartEndMs,
            rampUpStartTime: rampUpStartTime,
            rampUpEndTime: rampUpEndTime,
            rampDownStartTime: rampDownStartTime,
            rampDownEndTime: rampDownEndTime,
            bonusStartTime: bonusStartTime,
            bonusEndTime: bonusEndTime,
            hasBonusMinutes: true,
            tz: 'string',
            reportingInterval: product.reporting_interval_ms
        };
    }


    handleChart = (event: Event, eventPerformance: NodePerformance[], additionalChartData: AdditionalChartData) => {
        if (!this.product) {
            console.log('Expected Product to be set.')
            return;
        }
        const {event_node_statistics: {active_event_nodes}} = event;
        const eventPerformanceData: NodePerformanceForChart | null = this.getEventPerformance(event, eventPerformance);

        if (active_event_nodes > 0) {
            this.createChart(event, this.product, eventPerformanceData, this.eventNode, eventPerformance, additionalChartData);
            this.populatePlotBand(this.eventNode, additionalChartData);
        }
    }

    private createBanner = (event: Event, product: Product): BannerDetails => {
        const {
            average_performance_status,
            last_current_performance_status,
            registration_type, last_current_performance_dttm_utc,
            expected_capacity_value, event_node_end_dttm_utc
        } = this.eventNode;

        if (product.target_type === 'RANGE' && registration_type === 'LOAD_DROP_TO') {
            return this.bannerDetailsStatusType['NO_DATA'];
        }

        const {full_time_zone, program_time_zone_abbr, event_progress_status} = event;
        this.eventStatus = event_progress_status === 'AFTER' ? average_performance_status : last_current_performance_status;
        let {title, backgroundColor, borderColor, iconName} = this.bannerDetailsStatusType['NO_DATA'];
        const eventFinished = moment().isAfter(moment(event_node_end_dttm_utc)) ? 'AFTER' : 'NOT_AFTER';
        if (this.eventStatus != null) {
            title = this.bannerDetailsStatusType[eventFinished][this.eventStatus].title;
            backgroundColor = this.bannerDetailsStatusType[eventFinished][this.eventStatus].backgroundColor;
            borderColor = this.bannerDetailsStatusType[eventFinished][this.eventStatus].borderColor;
            iconName = this.bannerDetailsStatusType[eventFinished][this.eventStatus].iconName;
        }
        const formattedDate = this.formatDate(last_current_performance_dttm_utc, full_time_zone, program_time_zone_abbr);
        let description;
        if (['NO_PERF_NUMBERS', 'ONLY_PERF_AVAILABLE', 'ONLY_TARGET_AVAILABLE'].includes(this.eventStatus) || !this.eventStatus) {
            description = this.bannerDetailsStatusType['NO_DATA'].description;
        } else {
            if (registration_type === "LOAD_DROP_TO") {
                if (this.eventStatus && product) {
                    description = this.setTextBannerLoadToDrop(event, product, formattedDate, eventFinished);
                }
            } else {
                description = this.setTextBanner(event, product, expected_capacity_value, formattedDate, eventFinished);
            }
        }

        return {
            title,
            description,
            backgroundColor,
            borderColor,
            iconName,
        }
    }

    getRegistration(registration_id: string) {
        this.chartService.getRegistration(registration_id).pipe(this.catchError()).subscribe(
            (registration: Registration) => {
                this.isRegistrationLoading = false;
                this.activeDispatchText.energy_reduction_plan = registration.energy_reduction_plan?.replace(/\\n/g, '');
                this.registration = registration;
            }
        )
    }

    getEventPerformance(event: Event, eventPerformance: NodePerformance[]) {
        if (!eventPerformance.length) return null;
        const {event_start_dttm_utc, event_end_dttm_utc} = event;

        const labels = {
            target: this.translate.instant("INTERVAL_GRAPH.TOOLTIP_TARGET"),
            demand: this.translate.instant("INTERVAL_GRAPH.TOOLTIP_DEMAND"),
            baseline: this.translate.instant("INTERVAL_GRAPH.TOOLTIP_BASELINE"),
            gen_load: this.translate.instant("INTERVAL_GRAPH.TOOLTIP_GEN_LOAD"),
            shortfall: this.translate.instant("INTERVAL_GRAPH.TOOLTIP_SHORTFALL"),
        };

        const datasetsBaseline: ChartDatasetsObject[] = [];
        const datasetsTarget: ChartDatasetsObject[] = [];
        const datasetsTargetLowerBound: ChartDatasetsObject[] = [];
        const datasetsTargetUpperBound: ChartDatasetsObject[] = [];
        const datasetsDemand: ChartDatasetsObject[] = [];

        const skipBaselines = [
            "LOAD_DROP_TO",
            "INVERSE_GENERATOR_TARGET",
        ].includes(this.eventNode?.registration_type);
        const useTargetBounds = this.product?.target_type === 'RANGE' || this.product?.target_type === 'RANGE_PLUS_HOUR';
        const valueMultiplier = this.eventNode?.registration_type === "INVERSE_GENERATOR_TARGET" ? -1 : 1;
        eventPerformance.forEach((nodePerformance: NodePerformance) => {
            const values = nodePerformance.model;
            const isTargetInRange = this.filterTargetRange(values.interval_dttm_utc, event_start_dttm_utc, event_end_dttm_utc);
            if (!skipBaselines && (values.baseline_value || values.baseline_value === 0)) {
                datasetsBaseline.push({
                    x: values.interval_dttm_utc,
                    y: values.baseline_value * valueMultiplier,
                    performance_value: values.performance_value
                });
            }
            if (values.target_value || values.target_value === 0) {
                datasetsTarget.push({
                    x: values.interval_dttm_utc,
                    y: isTargetInRange ? values.target_value * valueMultiplier : null,
                    performance_value: values.performance_value
                });
            }
            datasetsTargetLowerBound.push({
                x: values.interval_dttm_utc,
                y: useTargetBounds && isTargetInRange && values.lower_target_value !== null && values.lower_target_value !== undefined
                    ? values.lower_target_value * valueMultiplier
                    : null,
                performance_value: values.performance_value
            });
            datasetsTargetUpperBound.push({
                x: values.interval_dttm_utc,
                y: useTargetBounds && isTargetInRange && values.upper_target_value !== null && values.upper_target_value !== undefined
                    ? values.upper_target_value * valueMultiplier
                    : null,
                performance_value: values.performance_value
            });
            if (values.metered_value || values.metered_value === 0) {
                datasetsDemand.push({
                    x: values.interval_dttm_utc,
                    y: values.metered_value * valueMultiplier,
                    performance_value: values.performance_value
                });
            }
        })
        const nodePerformance: NodePerformanceForChart = {
            labels,
            datasetsBaseline,
            datasetsTarget,
            datasetsTargetLowerBound,
            datasetsTargetUpperBound,
            datasetsDemand,
            eventPerformance
        };
        return nodePerformance;
    }

    filterTargetRange(targetPointTime: string, eventStartTime: string, eventEndTime?: string | null) {
        const startTimeValue = moment(eventStartTime).valueOf();
        const timeValue = moment(targetPointTime).valueOf();

        if (Number.isNaN(startTimeValue) || Number.isNaN(timeValue)) {
            return false;
        }

        const endTimeValue = moment(eventEndTime).valueOf();
        if (Number.isNaN(endTimeValue)) {
            return timeValue >= (startTimeValue);
        }

        return timeValue >= (startTimeValue) && timeValue <= endTimeValue;
    }

    openDispatchInfo() {
        this.iconName = 'arrow-up';
        this.openDispatch = true;
    }

    closeDispatchInfo() {
        this.iconName = 'arrow-down';
        this.openDispatch = false;
    }

    openMore() {
        this.showMore = false;
        this.visibleCount = this.contacts.length;
        setTimeout(() => {
            showMoreCss();
        });
    }

    closeMore() {
        this.showMore = true;
        this.visibleCount = 3;
        setTimeout(() => {
            showMoreCss();
        });
    }

    setViewModel(
        node: EventNodeAPI, dataRange: [number, number | null],
        additionalChartData: AdditionalChartData,
    ) {
        const showBaselineAdjustment = productsWithBaselineAdjustment[environment.environment]?.includes(this.product?.id);
        this.viewModel = {
            from: additionalChartData.rampUpStartTime.valueOf(),
            to: additionalChartData.rampUpEndTime.valueOf(),
            start: additionalChartData.rampDownStartTime?.valueOf() || 0,
            end: additionalChartData.rampDownEndTime?.valueOf() || 0,
            start_bonus_minutes: additionalChartData.bonusStartTime?.valueOf() || 0,
            end_bonus_minutes: additionalChartData.bonusEndTime?.valueOf() || 0,
            dataRange: dataRange,
            demand: [],
            baseline: [],
            target: [],
            performance: [],
            baselineAdjFrom: moment(node.adjustment_window_start).startOf('minute').valueOf(),
            baselineAdjTo: moment(node.adjustment_window_end).startOf('minute').valueOf(),
            show_baseline_adjustment: showBaselineAdjustment,
            registration_type: node.registration_type,
        };
    }

    setTimeChart() {
        this.timeChart.startRamp = this.viewModel?.from;
        this.timeChart.endRamp = this.viewModel?.to;
        this.timeChart.startBaseline = this.viewModel?.baselineAdjFrom;
        this.timeChart.endBaseline = this.viewModel?.baselineAdjTo;
    }

    addPlotBand = (key: string, xMin: number, xMax: number, backgroundColor: string, zIndex: number) => {
        if (!this.chart) return;
        if (!this.chart.options) this.chart.options = {} as any;
        if (!this.chart.options.plugins) this.chart.options.plugins = {} as any;
        if (!this.chart.options.plugins.annotation) this.chart.options.plugins.annotation = {annotations: {}} as any;
        if (!this.chart.options.plugins.annotation.annotations) this.chart.options.plugins.annotation.annotations = {} as any;

        this.chart.options.plugins.annotation.annotations[key] = {
            type: 'box',
            xMin: moment.utc(xMin).toISOString(),
            xMax: moment.utc(xMax).toISOString(),
            backgroundColor,
            borderWidth: 0,
            borderColor: backgroundColor,
            drawTime: 'beforeDraw',
            z: zIndex,
        };
    };

    createRampPeriod() {
        const {from, to, start, end} = this.viewModel || {};

        if (from && to) {
            this.addPlotBand('box2', from, Math.floor(to / 60000) * 60000, 'rgb(241, 241, 242)', 3);
        }
        if (start && end) {
            this.addPlotBand('box3', start, end, 'rgb(241, 241, 242)', 2);
        }
    }

    createBaseLine() {
        const {baselineAdjFrom, baselineAdjTo, show_baseline_adjustment} = this.viewModel || {};
        if (baselineAdjFrom && baselineAdjTo && show_baseline_adjustment) {
            this.addPlotBand('box4', baselineAdjFrom, baselineAdjTo, 'rgba(219, 87, 255, 0.3)', 1);
        }
    }

    createBonusMinutes() {
        const {start_bonus_minutes, end_bonus_minutes} = this.viewModel || {};
        if (start_bonus_minutes) {
            this.addPlotBand('box1', start_bonus_minutes, Math.floor(end_bonus_minutes / 60000) * 60000, 'rgb(241, 241, 242)', 4);
        }
    }

    populatePlotBand(node: EventNodeAPI, additionalChartData: AdditionalChartData) {
        const dataRange: [number, number | null] = [additionalChartData.chartStartMs, additionalChartData.chartEndMs];
        this.setViewModel(node, dataRange, additionalChartData);
        this.setTimeChart();
        this.createRampPeriod();
        this.createBaseLine();
        this.createBonusMinutes();
        this.chart.update();
        return {
            ...this.viewModel,
        };
    }

    addRemoveLine(value: number) {
        const valueChart = this.chart.isDatasetVisible(value);
        if (valueChart === true) {
            this.chart.hide(value);
        }
        if (valueChart === false) {
            this.chart.show(value);
        }
    }

    openList() {
        this.openListElement = true;
    }

    showDRList() {
        this.sidebarOpenRequested.emit();
    }

    openListDownload() {
        this.listDownload = !this.listDownload;
    }

    async downloadChart(entry: string) {
        const canvas = document.getElementById('ADChart') as HTMLCanvasElement | null;
        if (!canvas) {
            return;
        }

        const metadataRows = [
            {
                label: this.translate.instant('DISPATCH_PERFORMANCE.SITE_INFO.SITE'),
                value: this.activeDispatchText.site || '-',
            },
            {
                label: this.translate.instant('DISPATCH_PERFORMANCE.SITE_INFO.DATE_TIME'),
                value: this.activeDispatchText.startAndEndTime || '-',
            },
            {
                label: this.translate.instant('DISPATCH_PERFORMANCE.SITE_INFO.PROGRAM'),
                value: this.activeDispatchText.program || '-',
            },
            {
                label: this.translate.instant('DISPATCH_PERFORMANCE.SITE_INFO.ORGANIZATION'),
                value: this.activeDispatchText.organization_name || '-',
            },
        ];
        await this.chartExportService.exportChart({
            chartCanvas: canvas,
            format: this.resolveExportFormat(entry),
            metadataRows,
            legendItems: this.buildExportLegendItems(),
            bannerDetails: this.bannerDetails,
            fileName: 'chart',
        });
    }

    private resolveExportFormat(entry: string): ChartExportFormat {
        if (entry.includes('JPEG')) {
            return 'JPEG';
        }
        if (entry.includes('SVG')) {
            return 'SVG';
        }
        if (entry.includes('PDF')) {
            return 'PDF';
        }
        return 'JPEG';
    }

    private buildExportLegendItems(): ChartExportLegendItem[] {
        if (!this.chart) {
            return [];
        }

        const legendItems: Array<ChartExportLegendItem | null> = [
            !this.showLineChart(0)
                ? {
                    label: this.translate.instant('INTERVAL_GRAPH.TOOLTIP_BASELINE'),
                    color: 'rgb(245, 86, 0)',
                    dimmed: !this.chart.isDatasetVisible(0),
                }
                : null,
            !this.showLineChart(1)
                ? {
                    label: this.translate.instant('INTERVAL_GRAPH.TOOLTIP_DEMAND'),
                    color: 'rgb(70, 30, 125)',
                    dimmed: !this.chart.isDatasetVisible(1),
                }
                : null,
            !this.showLineChart(2)
                ? {
                    label: this.translate.instant('INTERVAL_GRAPH.TOOLTIP_TARGET'),
                    color: 'rgb(53, 153, 143)',
                    dimmed: !this.chart.isDatasetVisible(2),
                }
                : null,
        ];

        return legendItems.filter(Boolean) as ChartExportLegendItem[];
    }

    formatPhoneNumber(contact: Contact) {
        const {country_code, phone_number} = contact.phone || {};

        if (!country_code || !phone_number) {
            return '';
        }
        const completePhone = `+${country_code}${phone_number}`;
        const phoneNumber = parsePhoneNumberFromString(completePhone);

        if (phoneNumber && phoneNumber.isValid()) {
            return `+${phoneNumber.countryCallingCode} ${phoneNumber.format('NATIONAL')}`;
        }

        return `+(${country_code}) ${phone_number}`;
    }

    onEmailClicked(contact: Contact) {
        const email = contact.email?.email_address;
        if (!email) return;
        const outlookUrl = `https://outlook.office.com/mail/deeplink/compose?to=${email}`;
        window.open(outlookUrl, '_blank');
    }

    onEmailRightClicked(contact: Contact) {
        const email = contact.email?.email_address;
        if (!email) return;

        this.clipboard.copy(email);

        this.activeTooltipEmailId = contact.email.id;

        setTimeout(() => {
            if (this.activeTooltipEmailId === contact.email.id) {
                this.activeTooltipEmailId = null;
            }
        }, 1200);
    }
}

export interface AdditionalChartData {
    chartStartTime: moment.Moment,
    chartEndTime: moment.Moment | null,
    chartStartMs: number,
    chartEndMs: number | null,
    rampUpStartTime: moment.Moment,
    rampUpEndTime: moment.Moment,
    rampDownStartTime: moment.Moment | null,
    rampDownEndTime: moment.Moment | null,
    bonusStartTime: moment.Moment | null,
    bonusEndTime: moment.Moment | null,
    hasBonusMinutes: boolean,
    tz: string,
    reportingInterval: number
}

const bannerStyle = {
    green: {
        backgroundColor: '#F5FFF6',
        borderColor: '#00855D',
        iconName: 'check-circle-fill',
    },
    orange: {
        backgroundColor: '#FCF1E6',
        borderColor: '#E67300',
        iconName: 'exclamation-circle-fill',
    },
    red: {
        backgroundColor: '#FFE9E8',
        borderColor: '#ED0529',
        iconName: 'exclamation-triangle-fill',
    },
    grey: {
        backgroundColor: '#EFF2F7',
        borderColor: '#272C34',
        iconName: 'x-circle-fill',
    }
}
